import React from "react";
import {
    Box,
    Button,
    Chip,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import useApiCall from "../../hooks/CancellableApiCall";
import {
    InternalAffairsLimitedComplaintResponse
} from "../../data/internal-affairs/InternalAffairsLimitedComplaintResponse";
import {
    InternalAffairsComplaintAssigneeResponse
} from "../../data/internal-affairs/InternalAffairsComplaintAssigneeResponse";
import {formatTimestamp} from "../../utils";
import {Link as RouterLink} from "react-router-dom";
import {InternalAffairsComplaintStatus} from "../../data/internal-affairs/InternalAffairsComplaintStatus";
import {
    InternalAffairsComplaintClassification
} from "../../data/internal-affairs/InternalAffairsComplaintClassification";
import ErrorIcon from '@mui/icons-material/Error';
import {LoadingContent} from "../LoadingContent";
import { InternalAffairsComplaintOutcome } from "../../data/internal-affairs/InternalAffairsComplaintOutcome";
import { RecordTypes } from "../../data/internal-affairs/RecordTypes";

interface ComplaintRowAssigneeProps {
    assignees: InternalAffairsComplaintAssigneeResponse[] | null
}

interface ComplaintRowProps {
    complaint: InternalAffairsLimitedComplaintResponse
    recordsView?: boolean
}

interface ComplaintTableProps {
    complaints: InternalAffairsLimitedComplaintResponse[] | null
    header?: string
    personal?: boolean
    recordsView?: boolean
}

interface InternalAffairsComplaintMasterListProps {
    endpoint: string
}

export const ComplaintTable = (props: ComplaintTableProps) => {
    return <>
        {props.complaints?.length ?? -1 > 0 ? <>
            <Typography variant="subtitle1">{props.header}</Typography>
            <Box sx={{overflowX:"auto"}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Complaint ID</TableCell>
                            <TableCell>Defendant</TableCell>
                            <TableCell>Creator</TableCell>
                            <TableCell>Classification</TableCell>
                            <TableCell>Status</TableCell>
                            {props.recordsView ? <>
                            <TableCell>Outcome</TableCell>
                            <TableCell>Action Taken</TableCell>
                            </> : 
                            <TableCell>Assigned Investigator(s)</TableCell>}
                            <TableCell>Creation Date</TableCell>
                            <TableCell> </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.complaints && props.complaints.map((complaint) => {
                            return <AccessibleComplaintRow key={complaint.displayId} complaint={complaint} recordsView={props.recordsView}/>
                        })
                        }
                    </TableBody>
                </Table>
            </Box>
        </> : <>
            <Typography sx={{ fontStyle: 'italic' }}>There are either no complaints accessible within this section, or you do not have access to the data in this section</Typography>
        </>}
    </>
}

const AssigneeCell = (props: ComplaintRowAssigneeProps) => {
    const MAX_ASSIGNEE_NAME_CHARS = 20
    const assignees = props.assignees
    if (!assignees || assignees.length === 0) {
        return <>None</>
    }

    const shown = assignees.slice(0, 2)
    const displayNodes = shown.flatMap((assignee, index) => {
        const separator = index > 0 ? ", " : ""
        if (assignee.nick.length > MAX_ASSIGNEE_NAME_CHARS) {
            return [
                separator,
                <Tooltip key={index} title={assignee.nick}>
                    <span>{assignee.nick.slice(0, MAX_ASSIGNEE_NAME_CHARS).trimEnd() + "…"}</span>
                </Tooltip>
            ]
        }
        return [separator + assignee.nick]
    })

    const remaining = assignees.slice(2)

    return <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="body2" component="span">{displayNodes}</Typography>
        {remaining.length > 0 && <Tooltip title={remaining.map((assignee) => assignee.nick).join(", ")}>
            <Chip
                size="small"
                label={`+${remaining.length}`}
                sx={{
                    borderRadius: "50%",
                    height: 24,
                    width: 24,
                    cursor: "default",
                    "& .MuiChip-label": {px: 0}
                }}
            />
        </Tooltip>}
    </Box>
}

const AccessibleComplaintRow = (props: ComplaintRowProps) => {
    let statusText
    if (props.complaint.status === "awaiting_investigator") {
        statusText = <Chip color={"warning"} label={"Awaiting Investigator"}/>
    } else if (props.complaint.status === "complaint_frozen") {
        statusText = statusText = <Chip color={"error"} label={"Complaint Frozen"}/>
    } else if (props.complaint.status === "ia_cmd_review") {
        statusText = statusText = <Chip color={"primary"} label={"Internal Affairs Command Review"}/>
    } else {
        statusText = InternalAffairsComplaintStatus.get(props.complaint.status)?.displayName
    }
    let anonymousCell = (!props.complaint.anonymous ? <Link underline="none" component={RouterLink} to={`/user/${props.complaint.creatorCommunityId}`}> {props.complaint.creatorName} </Link>: "Anonymous")
    return <TableRow>
        <TableCell> {props.complaint.displayId} </TableCell>
        <TableCell>
            <Link underline="none"
                  component={RouterLink}
                  to={`/user/${props.complaint.defendantCommunityId}`}
            > {props.complaint.defendantName} </Link>
        </TableCell>
        <TableCell>{anonymousCell}</TableCell>
        <TableCell> {InternalAffairsComplaintClassification.get(props.complaint.classification ?? "pending")?.displayName} </TableCell>
        <TableCell>
            {statusText} {props.complaint.overdue ? <Tooltip title="Complaint Overdue">
            <IconButton>
                <ErrorIcon color={"error"} />
            </IconButton>
        </Tooltip> : <></>}
        </TableCell>
        {props.recordsView ? <>
            <TableCell> {InternalAffairsComplaintOutcome.get(props.complaint.outcome ?? "pending")?.displayName} </TableCell>
        <TableCell> {RecordTypes.get(props.complaint.actionTaken ?? "")?.displayName} </TableCell>
        </> : <TableCell> <AssigneeCell assignees={props.complaint.assignees}/> </TableCell>}
        <TableCell> {formatTimestamp(props.complaint.creationDate)} </TableCell>
        <TableCell> <Button component={RouterLink} to={`/complaint/${props.complaint.displayId}`}
                            color="success">View Complaint</Button> </TableCell>
    </TableRow>
}

export const InternalAffairsComplaintMasterList = (props: InternalAffairsComplaintMasterListProps) => {
    const loadOpenComplaints = useApiCall<InternalAffairsLimitedComplaintResponse[]>({
        initialUrl: `/api/internal-affairs/${props.endpoint}`,
        redirectOnUnauthorized: false
    })

    return <LoadingContent showLoadingSkeleton={true} isLoading={loadOpenComplaints.isLoading || !loadOpenComplaints.data}>
        <ComplaintTable complaints={loadOpenComplaints.data} header={``}/>
    </LoadingContent>
}